/* eslint no-underscore-dangle: 0 */
/* eslint import/no-extraneous-dependencies: 0 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { setErrorNotification } from '../../../../redux/slices/NotificationSlice';
import ModalBox from '../../../../shared/component/ModalBox';
import TemplateContentUpload from '../TemplateContentUpload';
import HtmlHeader from '../HtmlHeader';
import utils from '../templateFunctions';
import FileFormat from '../FileFormat';
import { createOrUpdateTemplate } from '../../../../redux/slices/TemplateSlice';
import PopupBox from '../ContentUploadConfirmation';

function HtmlTemplateTwentySeven({ templateData }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState(false);
  const [openContentUploadConfirmation, setOpenContentUploadConfirmation] = useState(false);
  const [base64, setBase64] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [disable, setDisable] = useState(true);
  const [modalView, setModalView] = useState({ content: '', title: '' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const htmlRef = useRef(null);
  const convertBase64Ref = useRef(null);
  const convertHtmlRef = useRef(null);

  const cssStyles = `
    body {
      margin: 0;
      padding: 0;
    }
    .Festival-card {
      position: relative;
      font-family: "Montserrat", sans-serif;
      box-sizing: border-box;
      overflow: hidden;
      border-bottom: 24px solid #6C2D2C;
      height: 93.6vh;
    }
    .Festival-card__header {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 7px;
    }
    .Festival-card__header h3 {
      color: #6C2D2C;
    }
    .Festival-card__header img {
      width: 20px;
    }
    .Festival-card .main-container {
      height: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      padding: 50px;
    }
    .Festival-card__content {
      display: flex;
      justify-content: space-evenly;
      width: 100%;
      margin-block: 71px;
    }
    .Festival-card__image {
      width: clamp(250px, 60%, 500px);
      position: relative;
      z-index: 1;
    }
    .Festival-card__image::before {
      content: "";
      width: 100%;
      height: 80%;
      top: 14%;
      inset-inline-end: clamp(10px, 1.5vw, 30px);
      position: absolute;
      z-index: -1;
      background-color: #DCAA8F;
      border-top-right-radius: clamp(40px, 10vw, 120px);
    }
    .Festival-card__image img {
      width: 100%;
      height: 100%;
      border-top-right-radius: clamp(30px, 9vw, 110px);
    }
    .Festival-card .detils-Festival {
      display: flex;
      flex-direction: column;
      align-items: end;
      gap: 20px;
      padding-block: 30px;
      width: 50%;
    }
    .Festival-card .detils-Festival__title {
      font-weight: 700;
      font-size: clamp(40px, 8vw, 120px);
      color: #6C2D2C;
      font-family: "Times New Roman", serif;
    }
    .Festival-card .detils-Festival__desc {
      color: #6C2D2C;
      font-size: clamp(20px, 6vw, 100px);
      font-weight: 400;
      margin-block: 10px 20px;
      font-family: "Meie Script", cursive;
    }
    .Festival-card .detils-Festival__offer {
      font-size: clamp(20px, 4vw, 50px);
      font-weight: 700;
      border: 0;
      outline: none;
      cursor: pointer;
      color: #6C2D2C;
      font-family: "Times New Roman", serif;
    }
    .Festival-card .detils-Festival__offer span {
      font-family: "Playfair Display", serif;
    }
    .Festival-card .detils-Festival__site {
      font-size: clamp(16px, 2vw, 34px);
      font-weight: 700;
      border: 1px solid #6C2D2C;
      outline: none;
      cursor: pointer;
      padding: 10px 40px;
      color: #6C2D2C;
      background-color: #E7C4AD;
      font-family: "Times New Roman", serif;
    }
    @media (max-width: 991px) {
      .Festival-card {
        height: 100%;
      }
      .Festival-card__content {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 30px;
      }
      .Festival-card__image::before {
        display: none;
      }
      .Festival-card .detils-Festival {
        width: 100%;
        align-items: center;
        text-align: center;
      }
    }
  `;

  const convertImage = async (url) => {
    await utils.convertSignedUrl(url)
      .then((response) => {
        setDisable(false);
        dispatch(createOrUpdateTemplate(response));
      })
      .catch((error) => {
        setDisable(false);
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  convertBase64Ref.current = async () => {
    toPng(htmlRef?.current, { cacheBust: true })
      .then((dataUrl) => {
        setBase64(dataUrl);
        convertImage(dataUrl);
      })
      .catch((error) => {
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  convertHtmlRef.current = () => {
    if (htmlRef.current) {
      const content = htmlRef.current.outerHTML;
      const fullHtml = `
        <html>
          <head>
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link 
              href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Urbanist:wght@100..900&display=swap" 
              rel="stylesheet" />
              <style>${cssStyles}</style>
          </head>
          <body>${content}</body>
        </html>
      `;
      setHtmlContent(fullHtml); // Set the HTML content as a string
    }
  };

  useEffect(() => {
    utils?.initializer(templateData).then((res) => (setData(res)));
    utils?.reload();
  }, [templateData]);

  /* useEffect runs convertBase64 after data is updated */
  useEffect(() => {
    if (data?.length > 0 && data?.[0]?.template_contents?.items?.length > 0) {
      setTimeout(() => {
        convertBase64Ref.current?.();
      }, 1500);
      convertHtmlRef.current?.();
    }
  }, [data]);

  const handleFileType = (fileType) => {
    setModalView({
      title: 'content',
      content: <TemplateContentUpload
        base64={base64}
        setNotification={setNotification}
        fileType={fileType}
        htmlContent={htmlContent}
      />,
    });
  };

  const onClickContentUpload = () => {
    setNotification(true);
    setModalView({
      title: t('fileFormat'),
      content: <FileFormat
        setNotification={setNotification}
        handleFileType={handleFileType}
        setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
        data={data}
      />,
    });
  };

  const contentDetails = data?.[0]?.template_contents?.items?.[0]?.details;
  const backgroundColor = data?.[0]?.styles?.background;
  const backgroundImage = Array.isArray(data?.[0]?.template_backgroundImage_url)
    ? data?.[0]?.template_backgroundImage_url
    : [];

  return (
    <div>
      <HtmlHeader
        htmlData={data}
        disable={disable}
        click={onClickContentUpload}
      />

      <ModalBox
        status={notification}
        closeModal={setNotification}
        modalView={modalView}
      />

      <ModalBox
        status={openContentUploadConfirmation}
        closeModal={setOpenContentUploadConfirmation}
        modalView={{
          title: 'Confirmation',
          content: <PopupBox
            setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
            setNotification={setNotification}
          />,
        }}
      />

      <div className="header-height" />

      <div ref={htmlRef} className="Festival-card">

        <div style={{ backgroundColor: `${backgroundColor}` }} className="main-container">
          <div className="Festival-card__header">
            <img src={backgroundImage?.[0]} alt="" />
            <h3>{contentDetails?.[0]?.header?.toUpperCase()}</h3>
          </div>

          <div className="Festival-card__content">
            <div className="Festival-card__image">
              <img src={contentDetails?.[0]?.image} alt="Template Pic" />
              <span> </span>
            </div>

            <div className="detils-Festival">
              <h1 className="detils-Festival__title">{contentDetails?.[0]?.item_name?.toUpperCase()}</h1>

              <p className="detils-Festival__desc">{contentDetails?.[0]?.description}</p>

              <p className="detils-Festival__offer">
                DISCOUNT <span>{contentDetails?.[0]?.offer_percentage?.toUpperCase()}</span> OFF
              </p>

              <button type="button" className="detils-Festival__site">
                {contentDetails?.[0]?.website?.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

HtmlTemplateTwentySeven.propTypes = {
  templateData:
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      template_title: PropTypes.string.isRequired,
    }).isRequired,
  // Ensure templateData is an array of objects with specific shape
};

export default HtmlTemplateTwentySeven;
